import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MACRO_BINDING_KEYS } from "../MacroBindingContext";

export interface MacroMarkdownEditorModalProps {
  isOpen: boolean;
  value: string;
  onClose: () => void;
  onApply: (value: string) => void;
}

/**
 * Default markdown template for macros
 */
export const DEFAULT_MACRO_MARKDOWN = `Hi **{{FirstName}}**,

Thank you for your inquiry! We're excited to help you on your **fitness journey**.

Whether you're looking to lose body fat, recover your lost fitness levels, shape up or simply just looking to reset everything and restart afresh, we are the **perfect KICK-START SPECIALISTS for you!**

After all, **_This is what we do, and we do it well!_**

*"Our Goal is to Help You Achieve Yours, {{FirstName}}!"*

---

Please let me know if you have any questions.

Healthy regards,

**Liam**
`;

/**
 * The actual modal content component
 */
function MacroMarkdownEditorModalContent({
  value,
  onClose,
  onApply,
}: Omit<MacroMarkdownEditorModalProps, "isOpen">) {
  const [text, setText] = useState(value);
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  // Focus textarea when modal opens or when switching to edit tab
  useEffect(() => {
    if (activeTab === "edit" && textareaRef.current) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  const handleApply = () => {
    onApply(text);
  };

  const handleCopyDefaults = () => {
    setText(DEFAULT_MACRO_MARKDOWN);
  };

  const handleClear = () => {
    setText("");
  };

  const handleCancel = () => {
    onClose();
  };

  const insertVariable = (varName: string) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = text.substring(0, start);
      const after = text.substring(end);
      const newText = `${before}{{${varName}}}${after}`;
      setText(newText);
      // Restore cursor position after the inserted variable
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + varName.length + 4;
        textarea.focus();
      }, 0);
    }
  };

  // Prevent clicks inside modal from closing it
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex bg-black/40" onClick={handleCancel}>
      <div
        className="flex h-full w-full flex-col bg-white shadow-xl dark:bg-gray-900"
        onClick={handleModalClick}
      >
        <div className="flex items-center justify-between border-b px-4 py-3 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Markdown Editor
          </h2>
          <div className="flex items-center gap-4">
            {/* Tab buttons */}
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600">
              <button
                type="button"
                onClick={() => setActiveTab("edit")}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  activeTab === "edit"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                } rounded-l-md`}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("preview")}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  activeTab === "preview"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                } rounded-r-md`}
              >
                Preview
              </button>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="text-xs text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Close
            </button>
          </div>
        </div>

        {/* Variable insertion buttons */}
        <div className="flex items-center gap-2 border-b px-4 py-2 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
          <span className="text-[11px] text-gray-500 dark:text-gray-400">Insert variable:</span>
          {MACRO_BINDING_KEYS.map((varName) => (
            <button
              key={varName}
              type="button"
              onClick={() => insertVariable(varName)}
              className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-mono text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
            >
              {`{{${varName}}}`}
            </button>
          ))}
        </div>

        <div className="flex flex-1 flex-col gap-2 px-4 py-3 overflow-hidden">
          <div className="flex-1 overflow-hidden">
            {activeTab === "edit" ? (
              <textarea
                ref={textareaRef}
                className="h-full w-full resize-none rounded border border-gray-300 bg-white px-3 py-2 font-mono text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                style={{ minHeight: "60vh" }}
                value={text}
                onChange={(e) => setText(e.target.value)}
                spellCheck={false}
                placeholder={`# Enter your markdown here

Write your email content using **Markdown** syntax.

## Supported features
- **Bold** and *italic* text
- [Links](url)
- Lists
- Horizontal rules (---)
- {{Variables}} for personalization
`}
              />
            ) : (
              <div
                className="h-full w-full overflow-auto rounded border border-gray-300 bg-white px-4 py-3 dark:bg-gray-800 dark:border-gray-600"
                style={{ minHeight: "60vh" }}
              >
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {text || "*No content to preview*"}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-between pt-1">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopyDefaults}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              >
                Insert Template
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              >
                Clear
              </button>
              <span className="self-center text-[10px] text-gray-400 dark:text-gray-500">
                {text.length} chars
              </span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal dialog for editing Markdown with a code textarea and preview.
 * Uses React Portal to render at document body level.
 */
export function MacroMarkdownEditorModal({
  isOpen,
  value,
  onClose,
  onApply,
}: MacroMarkdownEditorModalProps) {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Create or find portal container
    let container = document.getElementById("macro-markdown-editor-modal-portal");
    if (!container) {
      container = document.createElement("div");
      container.id = "macro-markdown-editor-modal-portal";
      document.body.appendChild(container);
    }
    setPortalContainer(container);

    return () => {
      // Don't remove the container on unmount as other modals might use it
    };
  }, []);

  if (!isOpen || !portalContainer) return null;

  return ReactDOM.createPortal(
    <MacroMarkdownEditorModalContent value={value} onClose={onClose} onApply={onApply} />,
    portalContainer
  );
}

export default MacroMarkdownEditorModal;
